import { SettingsVoiceIcon } from '@/components/displays/CustomIcons';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CategoryIcon from '@mui/icons-material/Category';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DrawIcon from '@mui/icons-material/Draw';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InventoryIcon from '@mui/icons-material/Inventory';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import LinkIcon from '@mui/icons-material/Link';
import MicIcon from '@mui/icons-material/Mic';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PrintIcon from '@mui/icons-material/Print';
import SchoolIcon from '@mui/icons-material/School';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ShareIcon from '@mui/icons-material/Share';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StartIcon from '@mui/icons-material/Start';
import StyleIcon from '@mui/icons-material/Style';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import VideoStableIcon from '@mui/icons-material/VideoStable';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WallpaperIcon from '@mui/icons-material/Wallpaper';

import { FEATURE_TOGGLE_DISABLE_SENTENCE_MAKER } from '@/feature';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { Lesson } from '@/models/lesson';
import { useSession } from '@/providers/session/SessionProvider';
import { Box, Button, Divider, IconButton, Menu, MenuItem, Skeleton, Stack, styled, TextField, Tooltip } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';

const TITLE_MIN_WIDTH = '40%';

const LessonEditorHeaderContainer = styled('div')(({ theme }) => [
    {
        backgroundColor: (theme.vars || theme).palette.grey[200],
        boxShadow: theme.shadows[1],
    },
    theme.applyStyles('dark', {
        backgroundColor: (theme.vars || theme).palette.grey[800],
    }),
]);

const LessonEditorHeaderTitleTextField = styled(TextField)(({ theme }) => [
    {
        minWidth: TITLE_MIN_WIDTH,
        backgroundColor: (theme.vars || theme).palette.grey[200],
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
        },
    },
    theme.applyStyles('dark', {
        backgroundColor: (theme.vars || theme).palette.grey[800],
        'input::placeholder': {
            color: (theme.vars || theme).palette.grey[400],
        },
    }),
]);

type MenuItem = {
    type: 'item';
    icon?: React.ReactNode;
    label: string;
    value: string;
    disabled?: boolean;
};

type MenuSubheader = {
    type: 'subheader';
    label: string;
};

type MenuDivider = {
    type: 'divider';
};

type MenusOption = MenuItem | MenuSubheader | MenuDivider;

type MenusProps = {
    title?: string;
    startIcon?: React.ReactNode;
    options?: MenusOption[];
    onClose?: (value: string | null) => void;
    editable: boolean;
};

function Menus({ title, startIcon, options, onClose, editable }: MenusProps) {
    const { isMobile } = useMedia();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (value: string | null) => {
        setAnchorEl(null);
        if (onClose) {
            onClose(value);
        }
    };

    return (
        <div>
            <Button
                disabled={!editable}
                sx={[
                    { pl: 2, whiteSpace: 'nowrap' },
                    (theme) =>
                        theme.applyStyles('light', {
                            '&:hover': {
                                backgroundColor: (theme.vars || theme).palette.grey[100],
                            },
                        }),
                ]}
                disableElevation
                onClick={handleClick}
                startIcon={startIcon}
                endIcon={<ArrowDropDownIcon />}
            >
                {!isMobile ? title : null}
            </Button>
            <Menu
                id='demo-customized-menu'
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
                disableEnforceFocus
                disableRestoreFocus
                sx={(theme) => ({
                    '& .MuiMenuItem-root': {
                        '& .MuiSvgIcon-root': {
                            marginRight: theme.spacing(1.5),
                        },
                    },
                })}
            >
                {options?.map((option) => {
                    switch (option.type) {
                        case 'item':
                            return (
                                <MenuItem disabled={option.disabled === true} key={option.value} onClick={() => handleClose(option.value)} disableRipple>
                                    {option.icon}
                                    {option.label}
                                </MenuItem>
                            );
                        case 'divider':
                            return <Divider key={'divider'} />;
                        case 'subheader':
                            return <ListSubheader key={option.label}>{option.label}</ListSubheader>;
                    }
                })}
            </Menu>
        </div>
    );
}

type LessonEditorHeaderProps = {
    editable: boolean;
    lesson?: Lesson;
    onBack?: () => void;
    onTitleChange?: (title: string) => void;
    onStartLesson?: (lesson: Lesson) => void;
    onCreateComponent?: (componentType: string) => void;
    onShareClick?: () => void;
    onPrintClick?: () => void;
};

export const LessonEditorHeader: React.FC<LessonEditorHeaderProps> = ({ lesson, onBack, onTitleChange, onStartLesson, editable, onCreateComponent, onShareClick, onPrintClick }) => {
    const { t } = useTranslation();
    const { session } = useSession();

    const { isMobile } = useMedia();
    const debounced = useDebounce(onTitleChange || (() => {}), 500);

    const legacyQuestionEnabledSchools = [
        // School Name of schools that have access to legacy hkcs questions
        // '旅龜小學',
        'HKCS',
    ];

    const showLegacy = !!session?.user?.teacherProfile?.schools?.some((school) => legacyQuestionEnabledSchools.includes(school.schoolName));

    return (
        <LessonEditorHeaderContainer>
            <Box sx={{ display: 'flex', flexGrow: 1, p: 1, px: 2, justifyContent: 'space-between', gap: 2 }}>
                <Tooltip title={t('common.back')}>
                    <IconButton onClick={onBack} aria-label='back'>
                        <ChevronLeftIcon />
                    </IconButton>
                </Tooltip>

                {lesson ? (
                    <LessonEditorHeaderTitleTextField
                        disabled={!editable}
                        slotProps={{
                            htmlInput: {
                                style: { textAlign: 'center', fontWeight: 600, fontSize: 18 },
                                maxLength: 128,
                                minLength: 1,
                            },
                        }}
                        placeholder='Input Title Here...'
                        defaultValue={lesson.latestLessonDraft.lessonName}
                        onChange={(e) => debounced?.(e.target.value)}
                    />
                ) : (
                    <Skeleton width={TITLE_MIN_WIDTH} height={40} variant='rounded' />
                )}

                <Button
                    variant='contained'
                    startIcon={isMobile ? undefined : <StartIcon />}
                    onClick={() => {
                        if (lesson) {
                            onStartLesson?.(lesson);
                        }
                    }}
                >
                    {!isMobile ? t('lesson.actions.startLesson') : <StartIcon />}
                </Button>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', flexGrow: 1, p: 1, px: 2 }}>
                <Stack direction='row' spacing={1}>
                    <Menus
                        editable={editable}
                        title={t('editor.header.assets.title')}
                        startIcon={<PermMediaIcon />}
                        options={[
                            { type: 'item', label: t('slide.asset.image'), value: 'image', icon: <InsertPhotoIcon fontSize='small' /> },
                            { type: 'item', label: t('slide.asset.text'), value: 'text', icon: <TextFieldsIcon fontSize='small' /> },
                            { type: 'item', label: t('slide.asset.audio'), value: 'audio', icon: <VolumeUpIcon fontSize='small' /> },
                            { type: 'item', label: t('slide.asset.link'), value: 'link', icon: <LinkIcon fontSize='small' /> },
                            { type: 'divider' },
                            { type: 'item', label: t('slide.asset.background'), value: 'background', icon: <WallpaperIcon fontSize='small' /> },
                        ]}
                        onClose={(action) => {
                            if (action) {
                                onCreateComponent?.(action);
                            }
                        }}
                    />
                    <Menus
                        editable={editable}
                        title={t('editor.header.components.title')}
                        startIcon={<SportsEsportsIcon />}
                        options={[
                            {
                                type: 'item',
                                label: t('slide.components.dnd.title'),
                                value: 'dnd',
                                icon: <SelectAllIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.characterWriting.title'),
                                value: 'characterWriting',
                                icon: <AppRegistrationIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.characterCards.title'),
                                value: 'characterCards',
                                icon: <VideoStableIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.drawing.title'),
                                value: 'drawing',
                                icon: <DrawIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.audioRecord.title'),
                                value: 'audioRecord',
                                icon: <MicIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.pronunciationAssessment.title'),
                                value: 'pronunciationAssessment',
                                icon: <SettingsVoiceIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.strokeWriting.title'),
                                value: 'strokeWriting',
                                icon: <AppRegistrationIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.radicalWriting.title'),
                                value: 'radicalWriting',
                                icon: <AppRegistrationIcon fontSize='small' />,
                            },
                            {
                                type: 'item',
                                label: t('slide.components.mc.title'),
                                value: 'mc',
                                icon: <LibraryAddCheckIcon fontSize='small' />,
                            },
                            ...(!FEATURE_TOGGLE_DISABLE_SENTENCE_MAKER
                                ? ([
                                      {
                                          type: 'item',
                                          label: t('slide.components.sentenceMaker.title'),
                                          value: 'sentenceMaker',
                                          icon: <EditNoteIcon fontSize='small' />,
                                      },
                                  ] as const)
                                : []),
                        ]}
                        onClose={(action) => {
                            if (action) {
                                onCreateComponent?.(action);
                            }
                        }}
                    />

                    <Menus
                        editable={editable}
                        title={t('editor.header.legacy.title')}
                        startIcon={<InventoryIcon />}
                        options={[
                            { type: 'subheader', label: t('editor.header.legacy.subheader.builtin') },
                            { type: 'item', label: t('slide.legacy.character.title'), value: 'character', icon: <CategoryIcon fontSize='small' /> },
                            ...(showLegacy
                                ? ([
                                      { type: 'item', label: t('slide.legacy.hkcsQ1.title'), value: 'hkcsQ1', icon: <SchoolIcon fontSize='small' /> },
                                      { type: 'item', label: t('slide.legacy.hkcsQ2.title'), value: 'hkcsQ2', icon: <SchoolIcon fontSize='small' /> },
                                      { type: 'item', label: t('slide.legacy.hkcsQ3.title'), value: 'hkcsQ3', icon: <SchoolIcon fontSize='small' /> },
                                      { type: 'item', label: t('slide.legacy.hkcsEnQ1.title'), value: 'hkcsEnQ1', icon: <SchoolIcon fontSize='small' /> },
                                      { type: 'item', label: t('slide.legacy.hkcsEnQ2.title'), value: 'hkcsEnQ2', icon: <SchoolIcon fontSize='small' /> },
                                  ] as const)
                                : []),
                        ]}
                        onClose={(action) => {
                            if (action) {
                                onCreateComponent?.(action);
                            }
                        }}
                    />
                </Stack>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ gap: 0.5 }} display='flex'>
                    <IconButton disabled>
                        <StyleIcon />
                    </IconButton>
                    <Tooltip title={t('common.share')}>
                        <IconButton onClick={() => onShareClick?.()} disabled={!editable}>
                            <ShareIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.print')}>
                        <IconButton onClick={() => onPrintClick?.()} disabled={!editable}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Divider />
        </LessonEditorHeaderContainer>
    );
};
